import {useState, useEffect, useRef} from 'react';
import {useAtom} from 'jotai';
import {nanoid} from 'nanoid';
import {X, Calendar as CalendarIcon, AlertCircle} from 'lucide-react';
import {taskCreateOpenAtom, taskCreatePrefillAtom} from '@lib/atoms';
import {useCreateTask, useTaskCreationContext} from '@api/tasks/hooks';
import {useAuth} from '@api/auth';
import {
  analyticsInsightErrorMessage,
  useCreateAnalyticsInsightTask,
  useAnalyticsInsight,
} from '@api/analytics-insights';
import {useInsightReview} from '@features/review-insight';
import {
  type TaskPriority,
  type TaskStatus,
  TASK_PRIORITY_LABELS,
} from '@entities/task/model/types';
import {dataToday} from '@shared/lib/period';
import {toast} from 'sonner';
import {cn} from '@lib/utils';
import {Button} from '@ui/shadcn/button';
import {Checkbox} from '@ui/shadcn/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/shadcn/dialog';
import {Input} from '@ui/local/Input';
import {Textarea} from '@ui/local/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/shadcn/select';
import {DateTimeField} from '@ui/local/DateTimeField';
import {TaskCallField} from './TaskCallField';

const toIsoDay = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TASK_TODAY = toIsoDay(dataToday());

const PRIORITY_OPTIONS: {
  value: TaskPriority;
  label: string;
  classes: string;
}[] = [
  {
    value: 'high',
    label: TASK_PRIORITY_LABELS.high,
    classes: 'border-[var(--score-bad)] text-[var(--score-bad)]',
  },
  {
    value: 'medium',
    label: TASK_PRIORITY_LABELS.medium,
    classes: 'border-[var(--score-warn)] text-[var(--score-warn)]',
  },
  {
    value: 'low',
    label: TASK_PRIORITY_LABELS.low,
    classes: 'border-[var(--color-border)] text-[var(--color-secondary)]',
  },
];

/** Модаль создания обычной задачи или связанной задачи из analytics insight. */
export const TaskCreateModal = () => {
  const [open, setOpen] = useAtom(taskCreateOpenAtom);
  const [prefill, setPrefill] = useAtom(taskCreatePrefillAtom);
  const {currentUser} = useAuth();
  const creationContext = useTaskCreationContext(open);
  const assignees = creationContext.data?.assignees ?? [];
  const canAssignOthers = assignees.length > 1;
  const selfAssignee =
    assignees.find((person) => person.userSid === currentUser?.sid) ??
    assignees[0];
  const employees = assignees.map((person) => ({
    id: person.userSid,
    name: person.displayName,
  }));
  const observer = creationContext.data?.observer;
  const [withObserver, setWithObserver] = useState(false);
  const createTask = useCreateTask();
  const createInsightTask = useCreateAnalyticsInsightTask();
  const insightReview = useInsightReview();
  const [reviewAfterCreate, setReviewAfterCreate] = useState(false);
  const [createdFromInsight, setCreatedFromInsight] = useState<string | null>(
    null,
  );
  const sourceState = useAnalyticsInsight(
    reviewAfterCreate ? (prefill?.analyticsInsightSid ?? null) : null,
    ['my-state'],
  );
  const insightIdempotencyKeyRef = useRef<string | null>(null);
  const insightRequestSignatureRef = useRef<string | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [managerId, setManagerId] = useState<string>('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [initialStatus, setInitialStatus] = useState<TaskStatus>('planned');
  const [dueDate, setDueDate] = useState(TASK_TODAY);
  const [dueTime, setDueTime] = useState('');
  const [communicationId, setCommunicationId] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // При выборе из нескольких участников недоступный владелец звонка не
  // подменяется текущим пользователем: постановщик выбирает исполнителя явно.
  const selectedAssignee = canAssignOthers
    ? assignees.find(
        (person) =>
          person.userSid ===
          (managerId || prefill?.managerId || selfAssignee?.userSid),
      )
    : selfAssignee;
  const assigneeId = selectedAssignee?.userSid;
  const canObserveSelfTask = Boolean(
    observer && assigneeId === currentUser?.sid,
  );
  const observerUserSid =
    canObserveSelfTask && withObserver ? observer?.userSid : undefined;

  // Применить префилл при открытии
  useEffect(() => {
    if (open && prefill) {
      if (prefill.title) setTitle(prefill.title);
      if (prefill.text) setText(prefill.text);
      if (prefill.managerId) setManagerId(prefill.managerId);
      if (prefill.communicationId) setCommunicationId(prefill.communicationId);
      if (prefill.initialStatus) setInitialStatus(prefill.initialStatus);
      if (prefill.priority) setPriority(prefill.priority);
      insightIdempotencyKeyRef.current = null;
      insightRequestSignatureRef.current = null;
    }
    if (!open) {
      // Сброс при закрытии
      setTitle('');
      setText('');
      setManagerId('');
      setWithObserver(false);
      setPriority('medium');
      setInitialStatus('planned');
      setDueDate(TASK_TODAY);
      setDueTime('');
      setCommunicationId('');
      setErrors({});
      setReviewAfterCreate(false);
      setCreatedFromInsight(null);
      setPrefill(null);
      insightIdempotencyKeyRef.current = null;
      insightRequestSignatureRef.current = null;
    }
  }, [open, prefill, setPrefill]);

  if (!open) return null;

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (title.trim().length < 3) e.title = 'Минимум 3 символа';
    if (dueDate < TASK_TODAY) e.dueDate = 'Дедлайн не может быть в прошлом';
    if (!assigneeId) e.managerId = 'Выберите ответственного';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!currentUser?.sid) {
      toast.error('Не удалось определить текущего пользователя');
      return;
    }
    if (!createdFromInsight && !validate()) return;
    if (reviewAfterCreate && !sourceState.data?.myState) return;
    // Менеджеру нельзя передать другого исполнителя даже через префилл звонка.
    if (!createdFromInsight && !assigneeId) {
      toast.error('Выберите ответственного');
      return;
    }
    const manager = employees.find((emp) => emp.id === assigneeId);
    const managerName =
      manager?.name ||
      selectedAssignee?.displayName ||
      currentUser.displayName ||
      currentUser.email;
    let taskCreated = Boolean(createdFromInsight);
    try {
      if (prefill?.analyticsInsightSid) {
        if (!createdFromInsight) {
          const input = {
            title: title.trim(),
            text: text.trim() || null,
            managerUserSid: assigneeId,
            observerUserSid,
            communicationId: communicationId || null,
            priority,
            state:
              initialStatus === 'in_progress' ? 'in-progress' : initialStatus,
            dueDate,
          } as const;
          const signature = JSON.stringify(input);
          if (
            insightRequestSignatureRef.current !== signature ||
            !insightIdempotencyKeyRef.current
          ) {
            insightRequestSignatureRef.current = signature;
            insightIdempotencyKeyRef.current = nanoid();
          }
          const created = await createInsightTask.mutateAsync({
            insightSid: prefill.analyticsInsightSid,
            input,
            idempotencyKey: insightIdempotencyKeyRef.current,
          });
          setCreatedFromInsight(created.sid);
          taskCreated = true;
        }
        if (reviewAfterCreate && sourceState.data?.myState) {
          await insightReview.setReviewed({
            insightSid: prefill.analyticsInsightSid,
            reviewed: true,
            expectedRevision: sourceState.data.myState.revision,
            successMessage: 'Задача создана, инсайт отмечен рассмотренным',
          });
        }
      } else {
        const input = {
          title: title.trim(),
          text: text.trim() || undefined,
          managerId: assigneeId || undefined,
          managerName,
          priority,
          status: initialStatus,
          dueDate: dueTime ? `${dueDate}T${dueTime}:00` : dueDate,
          communicationId: communicationId || undefined,
          creator: (canAssignOthers ? 'rop' : 'manager') as 'rop' | 'manager',
          observerUserSid,
        };
        const signature = JSON.stringify(input);
        if (
          insightRequestSignatureRef.current !== signature ||
          !insightIdempotencyKeyRef.current
        ) {
          insightRequestSignatureRef.current = signature;
          insightIdempotencyKeyRef.current = nanoid();
        }
        await createTask.mutateAsync({
          ...input,
          idempotencyKey: insightIdempotencyKeyRef.current,
        });
      }
      if (!reviewAfterCreate) toast.success('Задача создана');
      setOpen(false);
    } catch (error) {
      toast.error(
        taskCreated
          ? `Задача создана, но отметка не сохранена. ${analyticsInsightErrorMessage(error)}`
          : prefill?.analyticsInsightSid
            ? analyticsInsightErrorMessage(error)
            : 'Не удалось создать задачу. Попробуйте ещё раз.',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (
          !createTask.isPending &&
          !createInsightTask.isPending &&
          !insightReview.isPending
        )
          setOpen(value);
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName='z-[calc(var(--z-modal-task)+2)]!'
        className='z-[calc(var(--z-modal-task)+3)]! flex max-h-[calc(100dvh-4rem)] flex-col gap-0 overflow-hidden rounded-2xl bg-[var(--color-surface)] p-0 sm:max-w-[520px]'
        onOpenAutoFocus={() => {
          openerRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const opener = openerRef.current;
          if (
            opener?.isConnected &&
            !opener.closest('[inert], [aria-hidden="true"]')
          ) {
            opener.focus({preventScroll: true});
          }
        }}
      >
        {/* Header */}
        <DialogHeader className='shrink-0 flex-row items-center justify-between px-5 py-3 text-left border-b border-[var(--color-border)]'>
          <DialogTitle className='text-sm font-semibold'>
            Создать задачу
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Укажите название, ответственного и срок выполнения задачи.
          </DialogDescription>
          <DialogClose asChild>
            <Button
              variant='ghost'
              size='icon'
              aria-label='Закрыть создание задачи'
              className='size-7 text-[var(--color-secondary)]'
            >
              <X size={15} />
            </Button>
          </DialogClose>
        </DialogHeader>

        {/* Body */}
        <div className='min-h-0 flex-1 overflow-y-auto p-5 space-y-4'>
          {/* Заголовок */}
          <div>
            <label className='block text-xs font-medium text-[var(--color-foreground)] mb-1'>
              Заголовок <span className='text-[var(--color-danger)]'>*</span>
            </label>
            <Input
              type='text'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='Что нужно сделать?'
              aria-invalid={!!errors.title}
              className='w-full'
            />
            {errors.title && (
              <p className='text-xs text-[var(--color-danger)] mt-1 flex items-center gap-1'>
                <AlertCircle size={11} /> {errors.title}
              </p>
            )}
          </div>

          {/* Описание */}
          <div>
            <label className='block text-xs font-medium text-[var(--color-foreground)] mb-1'>
              Описание
            </label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='Подробности...'
              rows={3}
              className='w-full resize-none'
            />
          </div>

          {/* Общие права формы: менеджер создаёт задачи только на себя. */}
          {canAssignOthers ? (
            <div>
              <label className='block text-xs font-medium text-[var(--color-foreground)] mb-1'>
                Ответственный{' '}
                <span className='text-[var(--color-danger)]'>*</span>
              </label>
              <Select
                value={assigneeId ?? ''}
                onValueChange={(value) => {
                  setManagerId(value);
                  setWithObserver(false);
                  setErrors((previous) => ({...previous, managerId: ''}));
                }}
              >
                <SelectTrigger
                  className='w-full'
                  aria-invalid={!!errors.managerId}
                >
                  <SelectValue placeholder='Выберите ответственного' />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {prefill?.managerId && !selectedAssignee && (
                <p className='mt-1.5 text-xs text-[var(--color-muted-foreground)]'>
                  Владельцу звонка нельзя назначить задачу напрямую. Выберите
                  ответственного из списка.
                </p>
              )}
              {errors.managerId && (
                <p className='mt-1 text-xs text-[var(--color-danger)]'>
                  {errors.managerId}
                </p>
              )}
            </div>
          ) : (
            <div>
              <label
                htmlFor='task-create-self-assignee'
                className='block text-xs font-medium text-[var(--color-foreground)] mb-1'
              >
                Ответственный
              </label>
              <Input
                id='task-create-self-assignee'
                value={
                  currentUser
                    ? `${currentUser.displayName || currentUser.email} (вы)`
                    : 'Загрузка пользователя…'
                }
                readOnly
                className='w-full'
              />
            </div>
          )}

          {observer && canObserveSelfTask && (
            <label className='flex items-start gap-2 text-xs'>
              <Checkbox
                checked={withObserver}
                onCheckedChange={(value) => setWithObserver(value === true)}
              />
              <span>
                Добавить наблюдателя — {observer.displayName} (ваш
                руководитель). Он сможет видеть задачу и писать комментарии.
              </span>
            </label>
          )}
          {creationContext.isError && (
            <p role='alert' className='text-xs text-[var(--color-danger)]'>
              Не удалось загрузить участников.{' '}
              <Button
                variant='ghost'
                size='xs'
                onClick={() => void creationContext.refetch()}
              >
                Повторить
              </Button>
            </p>
          )}

          {/* Приоритет (как радио-кнопки) */}
          <div>
            <label className='block text-xs font-medium text-[var(--color-foreground)] mb-1'>
              Приоритет
            </label>
            <div className='flex gap-2'>
              {PRIORITY_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => setPriority(opt.value)}
                  className={cn(
                    'flex-1 border-2',
                    priority === opt.value
                      ? opt.classes
                      : 'border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-secondary)]',
                  )}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Дедлайн */}
          <div>
            <label className='block text-xs font-medium text-[var(--color-foreground)] mb-1 flex items-center gap-1'>
              <CalendarIcon size={11} /> Дедлайн{' '}
              <span className='text-[var(--color-danger)]'>*</span>
            </label>
            {prefill?.analyticsInsightSid ? (
              <Input
                type='date'
                value={dueDate}
                min={TASK_TODAY}
                onChange={(event) => setDueDate(event.target.value)}
                aria-invalid={!!errors.dueDate}
                className='w-full'
              />
            ) : (
              <DateTimeField
                date={dueDate}
                time={dueTime}
                onDateChange={setDueDate}
                onTimeChange={setDueTime}
                minDate={TASK_TODAY}
                invalid={!!errors.dueDate}
                contentZIndex='calc(var(--z-modal-task) + 4)'
              />
            )}
            {errors.dueDate && (
              <p className='text-xs text-[var(--color-danger)] mt-1 flex items-center gap-1'>
                <AlertCircle size={11} /> {errors.dueDate}
              </p>
            )}
          </div>

          {/* Привязка к звонку — тот же тег с крестиком, что и в модалке задачи.
              Вручную звонок не вводится, поэтому без привязки блока нет. */}
          {communicationId && (
            <div>
              <label className='block text-xs font-medium text-[var(--color-foreground)] mb-1'>
                Звонок
              </label>
              <TaskCallField
                value={communicationId}
                canEdit
                onChange={setCommunicationId}
              />
            </div>
          )}
        </div>

        {prefill?.analyticsInsightSid && (
          <div className='space-y-2 px-5 py-3 text-sm'>
            {createdFromInsight ? (
              <p role='status'>
                Задача уже создана. Повторное действие сохранит только отметку
                инсайта.
              </p>
            ) : (
              <label className='flex items-center gap-2'>
                <Checkbox
                  checked={reviewAfterCreate}
                  onCheckedChange={(checked) =>
                    setReviewAfterCreate(checked === true)
                  }
                />
                После создания задачи отметить инсайт рассмотренным
              </label>
            )}
            {reviewAfterCreate && sourceState.isError && (
              <p role='alert'>
                {analyticsInsightErrorMessage(sourceState.error)}
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => void sourceState.refetch()}
                >
                  Обновить состояние
                </Button>
              </p>
            )}
          </div>
        )}
        {/* Footer */}
        <DialogFooter className='shrink-0 flex-row items-center justify-end gap-2 px-5 py-3 border-t border-[var(--color-border)] bg-[var(--color-muted)]'>
          <DialogClose asChild>
            <Button
              variant='outline'
              size='sm'
              className='text-[var(--color-secondary)]'
            >
              Отмена
            </Button>
          </DialogClose>
          <Button
            size='sm'
            onClick={handleSubmit}
            disabled={
              !currentUser?.sid ||
              (!createdFromInsight && !assigneeId) ||
              !creationContext.data ||
              creationContext.isFetching ||
              createTask.isPending ||
              createInsightTask.isPending ||
              insightReview.isPending ||
              (reviewAfterCreate && !sourceState.data?.myState)
            }
          >
            {createTask.isPending ||
            createInsightTask.isPending ||
            insightReview.isPending
              ? 'Сохранение…'
              : createdFromInsight
                ? 'Повторить отметку'
                : reviewAfterCreate
                  ? 'Создать и отметить'
                  : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
